import { FC, useMemo } from "react";
import { BarChart as RechartsBarChart, XAxis, Bar, YAxis } from "recharts";
import { GridLegacy as Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardFooter } from "./common/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
} from "./common/chart-config";
import { CSVDownloader } from "../fileDownload/csv-downloader";
import { IChartOptions, isChartOptions } from "./common/chart-options";
import { IChartProps } from "./common/types";
import { formatLabel } from "./common/format-helpers";
import { useUserParams } from "../../context/user-params-context";
import ChartIntervalSelector from "./selectors/chart-interval-selector";
import { useReleaseManagement, ReleaseFlag } from "../../utility/feature-management";
import { ChartInfoTooltip } from "./common/card-info-tooltip";
import { useChartData } from "../../hooks/useChartData";
import { resolveChartTitle } from "./common/resolve-chart-title";
import { usePreferences } from "../../utility/usePreferences";
import { capitalise } from "../../utility/utils";

interface BarOptions {
  fill?: string;
  dataKey?: string;
}

interface BarChartData {
  Date?: string;
  Vehicles?: number;
  Spaces?: number;
  [key: string]: string | number | undefined;
}

interface IBarChartOptions extends IChartOptions {
  bar?: Record<string, BarOptions>;
  isStacked?: boolean;
}

export const BarChart: FC<IChartProps> = ({ uuid }) => {
  const { t } = useTranslation();
  const { userParams, parameterTypes } = useUserParams();
  const { distancePerHour, distanceUnitText, distanceUnitSingular } = usePreferences();

  const releaseChartIntervalSelector = useReleaseManagement(
    ReleaseFlag.releaseChartIntervalSelector
  );
  const releaseRefactorChartTitle = useReleaseManagement(
    ReleaseFlag.releaseRefactorChartTitle
  );

  const { data, isLoading } = useChartData<BarChartData[], IBarChartOptions>(
    uuid,
    parameterTypes,
    userParams,
    { validateOptions: isChartOptions }
  );

  const chartOptions = data?.options;
  const chartData = data?.data || [];
  const chartName = data?.name;
  const chartTranslationKey = data?.translationKey ?? undefined;

  const chartConfig: ChartConfig = Object.entries(
    chartOptions?.bar || {}
  ).reduce((acc, [key, barOptions]) => {
    acc[key] = {
      label: key,
      color: barOptions.fill || `var(--color-${key})`,
    };
    return acc;
  }, {} as ChartConfig);

  const computedTitle = useMemo(() => {
    return resolveChartTitle({
      t,
      name: chartName ?? undefined,
      translationKey: chartTranslationKey,
      featureEnabled: releaseRefactorChartTitle,
      defaultText: chartName ?? undefined,
      i18nParams: {
        distancePerHour,
        distanceUnit: capitalise(distanceUnitText),
        distanceUnitSingular: capitalise(distanceUnitSingular),
        timeFormat: "hh:mm",
      },
    });
  }, [
    t,
    chartName,
    chartTranslationKey,
    releaseRefactorChartTitle,
    distancePerHour,
    distanceUnitText,
    distanceUnitSingular,
  ]);

  return (
    <Grid item xs={12} md={8} lg={6}>
      <Card
        style={{
          animation: isLoading ? "pulse 2s linear infinite" : undefined,
        }}
      >
        {chartOptions?.description && (
          <ChartInfoTooltip description={chartOptions.description} />
        )}
        <CardContent>
          {releaseChartIntervalSelector && chartOptions?.enableDropdown && (
            <ChartIntervalSelector />
          )}
          <ChartContainer config={chartConfig}>
            <RechartsBarChart data={chartData}>
              <XAxis
                dataKey={chartOptions?.xAxis?.dataKey || "name"}
                label={{
                  value: formatLabel(userParams, chartOptions?.xAxis?.label),
                  position: "insideBottom",
                  offset: chartOptions?.xAxis?.offset || 0,
                }}
                angle={chartOptions?.xAxis?.angle}
                interval={chartOptions?.xAxis?.interval}
                tickFormatter={(value) =>
                  formatLabel(userParams, {
                    ...chartOptions?.xAxis?.tick?.label,
                    value,
                  })
                }
              />
              <YAxis
                dataKey={chartOptions?.yAxis?.dataKey}
                allowDuplicatedCategory={false}
                label={{
                  value: formatLabel(userParams, chartOptions?.yAxis?.label),
                  angle: chartOptions?.yAxis?.angle || -90,
                  position: "insideLeft",
                  offset: 0,
                }}
                interval={chartOptions?.yAxis?.interval}
                tickFormatter={(value) =>
                  formatLabel(userParams, {
                    ...chartOptions?.yAxis?.tick?.label,
                    value,
                  })
                }
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    heading={chartOptions?.tooltip?.heading}
                    nameKey={chartOptions?.tooltip?.nameKey}
                    labelFormatter={(value) =>
                      formatLabel(userParams, {
                        ...chartOptions?.tooltip?.label,
                        value,
                      })
                    }
                  />
                }
              />
              {chartOptions?.showLegend && (
                <ChartLegend
                  verticalAlign="top"
                  align="right"
                  layout="vertical"
                  formatter={(value) => (
                    <span style={{ color: "black" }}>{value}</span>
                  )}
                />
              )}
              {Object.entries(chartOptions?.bar || {}).map(
                ([key, barOptions]) => {
                  const dataKey = barOptions.dataKey;
                  const fill = barOptions.fill || `var(--color-${key})`;
                  const stackId = chartOptions?.isStacked ? "a" : undefined;
                  const radius = chartOptions?.isStacked ? 0 : 4;
                  return dataKey ? (
                    <Bar
                      key={key}
                      dataKey={dataKey}
                      fill={fill}
                      stackId={stackId}
                      radius={[radius, radius, 0, 0]}
                    />
                  ) : null;
                }
              )}
            </RechartsBarChart>
          </ChartContainer>
        </CardContent>
        <CardFooter>
          <Typography variant="subtitle1">
            {isLoading ? t("general.loading") : computedTitle}
          </Typography>
          {chartOptions?.enableCSVDownload && (
            <CSVDownloader data={chartData} filename={chartName || ""} />
          )}
        </CardFooter>
      </Card>
    </Grid>
  );
};

export default BarChart;
